import axios from 'axios';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import 'styles/css/index.css';
import App from 'js/components/App';
import ErrorBoundary from 'js/components/ErrorPages/ErrorBoundary';
import { ToastContainer } from 'react-toastify';
import { I18nextProvider } from 'react-i18next';
import { useEffect } from 'react';
import i18n from './js/i18n';
import { AuthProvider } from 'js/contexts/AuthContext';
import { PartnerUserFormsProvider } from 'js/contexts/PartnerUserFormsContext';
import { setAuthTokenProvider } from '@sysmobil/synoro-packages';
import authToken from 'js/library/utils/API/authToken';
import { installAuthInterceptor } from 'js/library/utils/API/auth/authInterceptor';
import { validateSessions } from 'js/library/utils/API/auth/validateSessions';
import cfac22 from 'js/library/utils/cfac22';
import { hasCompleteAuthSession } from 'js/library/services/authSession';

setAuthTokenProvider(authToken);

const authenticatedHosts = [
  cfac22('API_HOST_V1'),
  cfac22('API_HOST_V2'),
  cfac22('MICROSERVICES'),
].filter(Boolean);

axios.interceptors.request.use((config) => {
  if (
    typeof config?.url === 'string' &&
    authenticatedHosts.some((host) => config.url.startsWith(host))
  ) {
    return { ...config, withCredentials: true };
  }

  return config;
});

installAuthInterceptor();

// Forçar HTTPS em produção
if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
  window.location.href = window.location.href.replace('http:', 'https:');
}

const BrowserTabSession = () => {
  useEffect(() => {
    if (!sessionStorage.getItem('open_page')) {
      sessionStorage.setItem('open_page', String(Date.now()));
    }
  }, []);

  return null;
};

const triiboTheme = createTheme({
  palette: {
    primary: {
      main: '#6E3296',
    },
    secondary: {
      main: '#8B8B8B',
    },
  },
  typography: {
    useNextVariants: true,
    fontFamily: ['Source Sans Pro', 'sans-serif'].join(','),
    fontSize: 15,
    color: {
      primary: '#6E3296',
      secondary: '#707070',
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('triibo-web-app'));

const renderApplication = () => {
  root.render(
    <BrowserRouter>
      <ThemeProvider theme={triiboTheme}>
        <I18nextProvider i18n={i18n}>
          <AuthProvider>
            <BrowserTabSession />
            <PartnerUserFormsProvider>
              <ToastContainer autoClose={4000} />
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </PartnerUserFormsProvider>
          </AuthProvider>
        </I18nextProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

const bootstrapApplication = async () => {
  const hasPersistedAuthenticatedSession = hasCompleteAuthSession();

  if (!hasPersistedAuthenticatedSession) {
    renderApplication();
    return;
  }

  try {
    const validation = await validateSessions({ force: true });

    if (validation?.success !== false || window.location.pathname === '/') {
      renderApplication();
    }
  } catch {
    // validateSessions limpa a sessão inválida e redireciona para '/login'.
    if (window.location.pathname === '/') renderApplication();
  }
};

bootstrapApplication();
